import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './InvoicesList.css';

const formatDateValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getPresetRange = (preset) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const start = new Date(today);

  if (preset === 'today') {
    return { fromDate: formatDateValue(start), toDate: formatDateValue(today) };
  }

  if (preset === 'last7') {
    start.setDate(start.getDate() - 6);
    return { fromDate: formatDateValue(start), toDate: formatDateValue(today) };
  }

  if (preset === 'month') {
    start.setDate(1);
    return { fromDate: formatDateValue(start), toDate: formatDateValue(today) };
  }

  start.setDate(start.getDate() - 29);
  return { fromDate: formatDateValue(start), toDate: formatDateValue(today) };
};

export default function InvoicesList() {
  const defaultRange = getPresetRange('last30');
  const [documents, setDocuments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [receiptAccounts, setReceiptAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [rangePreset, setRangePreset] = useState('last30');
  const [fromDate, setFromDate] = useState(defaultRange.fromDate);
  const [toDate, setToDate] = useState(defaultRange.toDate);
  const [archivedOnly, setArchivedOnly] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [dueDialog, setDueDialog] = useState({ open: false, document: null, employeeName: '', dueAmount: '', receiptAccountCode: '11101' });
  const [receiptDialog, setReceiptDialog] = useState({ open: false, document: null, amount: '', receiptAccountCode: '11101' });
  const [paidDialog, setPaidDialog] = useState({
    open: false,
    document: null,
    receiptAccountCode: '11101',
    discountAmount: '',
    discountEmployeeName: '',
  });
  const [advanceDialog, setAdvanceDialog] = useState({ open: false, document: null, amount: '', receiptAccountCode: '11101' });
  const navigate = useNavigate();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearchTerm(searchInput.trim());
    }, 250);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let isMounted = true;

    const fetchDocuments = async () => {
      try {
        setLoading(true);
        setError('');
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/jobcards', {
          params: {
            search: searchTerm,
            fromDate,
            toDate,
            archivedOnly,
          },
          headers: { Authorization: `Bearer ${token}` },
        });

        if (isMounted) {
          setDocuments(response.data.jobCards || []);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.response?.data?.message || 'Failed to load Inv & Est documents');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchDocuments();

    return () => {
      isMounted = false;
    };
  }, [archivedOnly, fromDate, refreshKey, searchTerm, toDate]);

  useEffect(() => {
    let isMounted = true;

    const fetchEmployees = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/hr/employees', {
          params: { status: 'active' },
          headers: { Authorization: `Bearer ${token}` },
        });

        if (isMounted) {
          setEmployees(response.data.employees || []);
        }
      } catch (err) {
        console.error('Failed to load employees for due reference:', err);
      }
    };

    fetchEmployees();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchReceiptAccounts = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/accounting/chart-of-accounts', {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (isMounted) {
          const allowedClasses = ['Cash', 'Bank', 'MobileBank'];
          const accounts = (response.data.accounts || []).filter((account) => (
            account.IsPostable
            && account.IsActive
            && allowedClasses.includes(account.AccountClass)
          ));
          setReceiptAccounts(accounts);
        }
      } catch (err) {
        console.error('Failed to load receipt accounts:', err);
      }
    };

    fetchReceiptAccounts();

    return () => {
      isMounted = false;
    };
  }, []);

  const reloadDocuments = () => {
    setRefreshKey((current) => current + 1);
  };

  const handlePresetChange = (event) => {
    const nextPreset = event.target.value;
    setRangePreset(nextPreset);

    if (nextPreset === 'custom') {
      return;
    }

    const range = getPresetRange(nextPreset);
    setFromDate(range.fromDate);
    setToDate(range.toDate);
  };

  const handleFromDateChange = (event) => {
    const nextFromDate = event.target.value;
    setRangePreset('custom');
    setFromDate(nextFromDate);

    if (toDate && nextFromDate && nextFromDate > toDate) {
      setToDate(nextFromDate);
    }
  };

  const handleToDateChange = (event) => {
    const nextToDate = event.target.value;
    setRangePreset('custom');
    setToDate(nextToDate);

    if (fromDate && nextToDate && nextToDate < fromDate) {
      setFromDate(nextToDate);
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    return new Date(dateValue).toLocaleDateString('en-GB');
  };

  const formatMoney = (amount) => `Tk ${Number(amount || 0).toFixed(2)}`;
  const formatMoneyOrBlank = (amount) => {
    const numericAmount = Number(amount || 0);
    return numericAmount === 0 ? '' : formatMoney(numericAmount);
  };
  const getDefaultReceiptAccountCode = () => {
    if (receiptAccounts.some((account) => account.AccountCode === '11101')) {
      return '11101';
    }

    return receiptAccounts[0]?.AccountCode || '11101';
  };
  const getDueAmount = (item) => {
    const balanceDue = Number(item.BalanceDue || 0);
    if (Number.isFinite(balanceDue) && balanceDue > 0) {
      return balanceDue;
    }

    const totalAmount = Number(item.TotalAmount || 0);
    const paidAmount = Number(item.PaidAmount || 0);
    const discountAmount = Number(item.DiscountAmount || 0);
    return Math.max(0, Number((totalAmount - paidAmount - discountAmount).toFixed(2)));
  };

  const getInvoiceStatus = (item) => {
    const rawStatus = String(item.InvoiceStatus || '').trim();
    if (rawStatus.toLowerCase() === 'paid') {
      return 'Paid';
    }
    if (rawStatus.toLowerCase() === 'due') {
      return 'Due';
    }
    return rawStatus || 'Ready';
  };

  const isEstimateDocument = (item) => String(item?.DocumentType || '').trim().toLowerCase() === 'estimate';
  const isClosedDocument = (item) => String(item?.Status || '').trim().toLowerCase() === 'closed';
  const canReceiveAdvance = (item) => !isEstimateDocument(item) && !isClosedDocument(item);
  const canManageFinalPayment = (item) => !isEstimateDocument(item) && isClosedDocument(item);
  const isMarkedDue = (item) => getInvoiceStatus(item).toLowerCase() === 'due';
  const isMarkedPaid = (item) => getInvoiceStatus(item).toLowerCase() === 'paid';
  const getDocumentLabel = (item) => (isEstimateDocument(item) ? 'Estimate' : 'Job Card');

  const handleViewDocument = (item) => {
    if (!item?.JobCardId) {
      return;
    }

    if (isClosedDocument(item)) {
      navigate(`/invoices/${item.JobCardId}`);
      return;
    }

    navigate(
      isEstimateDocument(item)
        ? `/estimates/${item.JobCardId}/edit`
        : `/jobcards/${item.JobCardId}/edit`
    );
  };

  const handleCreateGatePass = async (item) => {
    try {
      setBusyId(item.JobCardId);
      const token = localStorage.getItem('token');
      await axios.post('/api/gatepass', {
        jobcard_number: String(item.JobNumber || item.JobCardId),
        car_number: String(item.CarNumber || ''),
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Gate-Pass created successfully!');
      setDocuments(current => current.map(doc => 
        doc.JobCardId === item.JobCardId ? { ...doc, GatePassStatus: 'Pending' } : doc
      ));
    } catch (err) {
      console.error(err);
      alert('Failed to create Gate-Pass');
    } finally {
      setBusyId(null);
    }
  };

  const receiveDuePayment = async (item, amount) => {
    const token = localStorage.getItem('token');

    try {
      setBusyId(item.JobCardId);
      setError('');
      setMessage('');

      const postingDate = formatDateValue(new Date());
      const invoiceRef = item.JobNumber || item.JobCardId;

      await axios.post('/api/accounting/operations/sales-post', {
        jobCardId: item.JobCardId,
        postingDate,
        referenceNo: String(invoiceRef),
        narration: `Auto sales posting before due receipt for invoice ${invoiceRef}`,
        autoPost: true,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const response = await axios.post('/api/accounting/operations/customer-receipt', {
        jobCardId: item.JobCardId,
        amount,
        postingDate,
        receiptAccountCode: receiptDialog.receiptAccountCode || getDefaultReceiptAccountCode(),
        referenceNo: `RCV-${invoiceRef}`,
        narration: `Additional due receipt from Inv & Est for invoice ${invoiceRef}`,
        autoPost: true,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setMessage(
        response.data?.invoiceStatus === 'Paid'
          ? 'Remaining due fully received and accounting updated.'
          : 'Partial due payment received and accounting updated.'
      );
      reloadDocuments();
      return true;
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to receive due payment');
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const updatePaymentStatus = async (item, paymentStatus, dueRefByEmployeeName = '', options = {}) => {
    const token = localStorage.getItem('token');

    if (!window.confirm(`Confirm before marking this invoice as ${paymentStatus.toLowerCase()}?`)) {
      return;
    }

    try {
      setBusyId(item.JobCardId);
      setError('');
      setMessage('');

      if (paymentStatus === 'Paid') {
        const postingDate = formatDateValue(new Date());
        const invoiceRef = item.JobNumber || item.JobCardId;
        const receiptAccountCode = options.receiptAccountCode || getDefaultReceiptAccountCode();
        const discountAmount = Number(options.discountAmount || 0);
        const discountRefByEmployeeName = String(options.discountEmployeeName || '').trim();
        const receiptAmount = Number(options.receivedAmount || 0);

        await axios.post('/api/accounting/operations/sales-post', {
          jobCardId: item.JobCardId,
          postingDate,
          referenceNo: String(invoiceRef),
          narration: `Auto sales posting from Inv & Est for invoice ${invoiceRef}`,
          autoPost: true,
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });

        await axios.post('/api/accounting/operations/customer-receipt', {
          jobCardId: item.JobCardId,
          amount: receiptAmount,
          discountAmount,
          discountRefByEmployeeName,
          postingDate,
          receiptAccountCode,
          referenceNo: `RCV-${invoiceRef}`,
          narration: `Auto receipt from Inv & Est for invoice ${invoiceRef}`,
          autoPost: true,
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setMessage(discountAmount > 0
          ? 'Invoice marked paid with discount and accounting receipt posted.'
          : 'Invoice marked paid and accounting receipt posted.');
      } else {
        const postingDate = formatDateValue(new Date());
        const invoiceRef = item.JobNumber || item.JobCardId;
        const receivedAmount = Number(options.receivedAmount || 0);
        const receiptAccountCode = options.receiptAccountCode || getDefaultReceiptAccountCode();

        await axios.post('/api/accounting/operations/sales-post', {
          jobCardId: item.JobCardId,
          postingDate,
          referenceNo: String(invoiceRef),
          narration: `Auto sales posting from Inv & Est due flow for invoice ${invoiceRef}`,
          autoPost: true,
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (receivedAmount > 0) {
          await axios.post('/api/accounting/operations/customer-receipt', {
            jobCardId: item.JobCardId,
            amount: receivedAmount,
            postingDate,
            receiptAccountCode,
            referenceNo: `RCV-${invoiceRef}`,
            narration: `Auto partial receipt from Inv & Est due flow for invoice ${invoiceRef}`,
            autoPost: true,
          }, {
            headers: { Authorization: `Bearer ${token}` },
          });
        }

        await axios.post(`/api/jobcards/${item.JobCardId}/payment-status`, {
          paymentStatus,
          dueRefByEmployeeName,
          receivedAmount,
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setMessage('Document marked as due and accounting synced.');
      }

      reloadDocuments();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to update payment status');
    } finally {
      setBusyId(null);
    }
  };

  const openDueDialog = (item) => {
    setError('');
    setMessage('');
    setDueDialog({
      open: true,
      document: item,
      employeeName: item.DueRefByEmployeeName || '',
      dueAmount: String(Number(item.BalanceDue || item.TotalAmount || 0).toFixed(2)),
      receiptAccountCode: getDefaultReceiptAccountCode(),
    });
  };

  const closeDueDialog = () => {
    setDueDialog({ open: false, document: null, employeeName: '', dueAmount: '', receiptAccountCode: getDefaultReceiptAccountCode() });
  };

  const openReceiptDialog = (item) => {
    setError('');
    setMessage('');
    setReceiptDialog({
      open: true,
      document: item,
      amount: String(getDueAmount(item).toFixed(2)),
      receiptAccountCode: getDefaultReceiptAccountCode(),
    });
  };

  const closeReceiptDialog = () => {
    setReceiptDialog({ open: false, document: null, amount: '', receiptAccountCode: getDefaultReceiptAccountCode() });
  };

  const openPaidDialog = (item) => {
    setError('');
    setMessage('');
    setPaidDialog({
      open: true,
      document: item,
      receiptAccountCode: getDefaultReceiptAccountCode(),
      discountAmount: String(Number(item.DiscountAmount || 0).toFixed(2)),
      discountEmployeeName: item.DiscountRefByEmployeeName || '',
    });
  };

  const closePaidDialog = () => {
    setPaidDialog({
      open: false,
      document: null,
      receiptAccountCode: getDefaultReceiptAccountCode(),
      discountAmount: '',
      discountEmployeeName: '',
    });
  };

  const openAdvanceDialog = (item) => {
    setError('');
    setMessage('');
    setAdvanceDialog({
      open: true,
      document: item,
      amount: getDueAmount(item) > 0 ? String(getDueAmount(item).toFixed(2)) : '',
      receiptAccountCode: getDefaultReceiptAccountCode(),
    });
  };

  const closeAdvanceDialog = () => {
    setAdvanceDialog({ open: false, document: null, amount: '', receiptAccountCode: getDefaultReceiptAccountCode() });
  };

  const confirmDueStatus = async () => {
    const employeeName = String(dueDialog.employeeName || '').trim();
    const dueAmount = Number(dueDialog.dueAmount || 0);

    if (!employeeName || !dueDialog.document) {
      setError('Select employee name for Due reference.');
      return;
    }

    const currentDueAmount = getDueAmount(dueDialog.document);
    if (!(dueAmount > 0) || dueAmount > currentDueAmount) {
      setError('Due amount must be greater than 0 and not more than current due amount.');
      return;
    }

    const receivedAmount = Number((currentDueAmount - dueAmount).toFixed(2));
    await updatePaymentStatus(dueDialog.document, 'Due', employeeName, {
      dueAmount,
      receivedAmount,
      receiptAccountCode: dueDialog.receiptAccountCode,
    });
    closeDueDialog();
  };

  const confirmReceiptDialog = async () => {
    if (!receiptDialog.document) {
      setError('Invoice not found for receiving due payment.');
      return;
    }

    const currentDueAmount = getDueAmount(receiptDialog.document);
    const receiveAmount = Number(receiptDialog.amount || 0);

    if (!(receiveAmount > 0) || receiveAmount > currentDueAmount) {
      setError('Receive amount must be greater than 0 and not more than current due amount.');
      return;
    }

    const success = await receiveDuePayment(receiptDialog.document, receiveAmount);
    if (success) {
      closeReceiptDialog();
    }
  };

  const confirmPaidDialog = async () => {
    if (!paidDialog.document) {
      setError('Invoice not found for payment.');
      return;
    }

    const currentDueAmount = getDueAmount(paidDialog.document);
    const discountAmount = Number(paidDialog.discountAmount || 0);
    const discountEmployeeName = String(paidDialog.discountEmployeeName || '').trim();

    if (discountAmount < 0 || discountAmount > currentDueAmount) {
      setError('Discount amount must be 0 or less than current due amount.');
      return;
    }

    if (discountAmount > 0 && !discountEmployeeName) {
      setError('Select employee name for discount reference.');
      return;
    }

    const receivedAmount = Number((currentDueAmount - discountAmount).toFixed(2));
    await updatePaymentStatus(paidDialog.document, 'Paid', '', {
      receiptAccountCode: paidDialog.receiptAccountCode,
      discountAmount,
      discountEmployeeName,
      receivedAmount,
    });
    closePaidDialog();
  };

  const confirmAdvanceDialog = async () => {
    if (!advanceDialog.document) {
      setError('Job card not found for advance payment.');
      return;
    }

    const amount = Number(advanceDialog.amount || 0);
    if (!(amount > 0)) {
      setError('Advance amount must be greater than 0.');
      return;
    }

    const token = localStorage.getItem('token');
    const postingDate = formatDateValue(new Date());
    const invoiceRef = advanceDialog.document.JobNumber || advanceDialog.document.JobCardId;

    try {
      setBusyId(advanceDialog.document.JobCardId);
      setError('');
      setMessage('');

      await axios.post('/api/accounting/operations/customer-advance', {
        jobCardId: advanceDialog.document.JobCardId,
        amount,
        postingDate,
        receiptAccountCode: advanceDialog.receiptAccountCode || getDefaultReceiptAccountCode(),
        referenceNo: `ADV-${invoiceRef}`,
        narration: `Advance receipt from Inv & Est for job card ${invoiceRef}`,
        autoPost: true,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setMessage('Advance payment received and accounting updated.');
      reloadDocuments();
      closeAdvanceDialog();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to receive advance payment');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="invoices-list-page app-fixed-page">
      <div className="invoices-list-header app-page-header">
        <div className="app-title-group">
          <button className="back-link app-back-button" onClick={() => navigate('/')} aria-label="Back to home">
            Back
          </button>
          <div className="app-title-copy">
            <h2>Inv & Est</h2>
            <p>Open and closed job cards and estimates appear here.</p>
          </div>
        </div>
        <div className="app-header-actions">
          <button className="btn-refresh-invoices" type="button" onClick={reloadDocuments}>
            Refresh
          </button>
          <button className="btn-new-invoice" onClick={() => navigate('/estimates/new')}>
            + New Estimate
          </button>
        </div>
      </div>

      <section className="invoices-toolbar" aria-label="Invoice filters">
        <select value={rangePreset} onChange={handlePresetChange} aria-label="Date range">
          <option value="last30">Last 30 Days</option>
          <option value="last7">Last 7 Days</option>
          <option value="today">Today</option>
          <option value="month">This Month</option>
          <option value="custom">Custom</option>
        </select>
        <input type="date" value={fromDate} onChange={handleFromDateChange} aria-label="From date" />
        <input type="date" value={toDate} onChange={handleToDateChange} aria-label="To date" />
        <label className="invoices-search">
          <span aria-hidden="true" />
          <input
            type="search"
            placeholder="Search by doc #, client, vehicle number, due, paid, or status"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
        </label>
        <label className="invoices-archive-filter">
          <input
            type="checkbox"
            checked={archivedOnly}
            onChange={(event) => setArchivedOnly(event.target.checked)}
          />
          Archived Only
        </label>
      </section>

      {message ? <div className="invoices-success">{message}</div> : null}
      <div className="app-scroll-results">
        {loading ? (
          <div className="invoices-loading">Loading invoices...</div>
        ) : error ? (
          <div className="invoices-error">{error}</div>
        ) : documents.length === 0 ? (
          <div className="no-invoices">
            <div className="no-invoices-icon">Doc</div>
            <div className="no-invoices-title">No Documents Found</div>
            <div className="no-invoices-desc">
              Create, close, or search a job card or estimate to see it here.
            </div>
          </div>
        ) : (
          <div className="invoices-table-wrap app-grid-scroll">
            <table className="invoices-table">
              <thead>
                <tr>
                  <th>Doc #</th>
                  <th>Type</th>
                  <th>Date</th>
                  <th>Client</th>
                  <th>Vehicle</th>
                  <th>Job Status</th>
                  <th>Amount</th>
                  <th>Adv Pay</th>
                  <th>Discount</th>
                  <th>Due Amount</th>
                  <th>Inv / Est Status</th>
                  <th>Due Ref</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((item) => (
                  <tr key={item.JobCardId}>
                    <td>
                      <strong>#{item.JobNumber || item.JobCardId}</strong>
                    </td>
                    <td>
                      <span className={`doc-type-badge doc-type-${String(item.DocumentType || 'JobCard').toLowerCase().replace(/\s+/g, '-')}`}>
                        {getDocumentLabel(item)}
                      </span>
                    </td>
                    <td>{formatDate(item.CreatedDate)}</td>
                    <td>{item.ClientName || '-'}</td>
                    <td>{item.CarNumber || '-'}</td>
                    <td>
                      <span className={`status-badge status-badge-${String(item.Status || 'Open').toLowerCase().replace(/\s+/g, '-')}`}>
                        {item.Status || 'Open'}
                      </span>
                    </td>
                    <td>{formatMoneyOrBlank(item.TotalAmount)}</td>
                    <td>{formatMoneyOrBlank(item.AdvancePaidAmount)}</td>
                    <td>{formatMoneyOrBlank(item.DiscountAmount)}</td>
                    <td className={getDueAmount(item) > 0 ? 'due-amount-cell' : ''}>
                      {formatMoney(getDueAmount(item))}
                    </td>
                    <td>
                      <span className={`status-badge status-badge-${getInvoiceStatus(item).toLowerCase().replace(/\s+/g, '-')}`}>
                        {getInvoiceStatus(item)}
                      </span>
                    </td>
                    <td>{item.DueRefByEmployeeName || '-'}</td>
                    <td>
                      <div className="invoice-action-group">
                        <button
                          className="btn-view-invoice"
                          onClick={() => handleViewDocument(item)}
                        >
                          View
                        </button>
                        {canReceiveAdvance(item) ? (
                          <button
                            className="btn-advance-invoice"
                            onClick={() => openAdvanceDialog(item)}
                            disabled={busyId === item.JobCardId}
                          >
                            {busyId === item.JobCardId ? 'Saving...' : 'Adv Payment'}
                          </button>
                        ) : null}
                        {canManageFinalPayment(item) ? (
                          <>
                            <button
                              className="btn-due-invoice"
                              onClick={() => openDueDialog(item)}
                              disabled={busyId === item.JobCardId || isMarkedDue(item) || isMarkedPaid(item)}
                            >
                              Due
                            </button>
                            {isMarkedDue(item) ? (
                              <button
                                className="btn-receive-invoice"
                                onClick={() => openReceiptDialog(item)}
                                disabled={busyId === item.JobCardId || isMarkedPaid(item)}
                              >
                                Receive
                              </button>
                            ) : null}
                            <button
                              className="btn-paid-invoice"
                              onClick={() => openPaidDialog(item)}
                              disabled={busyId === item.JobCardId || isMarkedPaid(item)}
                            >
                              {busyId === item.JobCardId ? 'Saving...' : 'Paid'}
                            </button>
                            {(isMarkedDue(item) || isMarkedPaid(item)) && (
                              item.GatePassStatus === 'Delivered' ? (
                                <span style={{ marginLeft: '10px', fontSize: '12px', color: '#4caf50', fontWeight: 'bold' }}>Delivery done</span>
                              ) : (
                                <label style={{ display: 'flex', alignItems: 'center', marginLeft: '10px', fontSize: '12px', cursor: 'pointer' }}>
                                  <input
                                    type="checkbox"
                                    disabled={busyId === item.JobCardId || item.GatePassStatus === 'Pending'}
                                    checked={item.GatePassStatus === 'Pending'}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        handleCreateGatePass(item);
                                      }
                                    }}
                                  />
                                  &nbsp;Gate-Pass
                                </label>
                              )
                            )}
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {dueDialog.open && dueDialog.document ? (
        <div className="invoice-dialog-backdrop" onClick={closeDueDialog}>
          <div className="invoice-dialog" onClick={(event) => event.stopPropagation()}>
            <h3>Make Due</h3>
            <p>
              Make sure before mark <strong>#{dueDialog.document.JobNumber || dueDialog.document.JobCardId}</strong> as due.
            </p>
            <label className="invoice-dialog-label">
              Invoice Total
              <input
                type="text"
                value={formatMoney(dueDialog.document.TotalAmount)}
                readOnly
              />
            </label>
            <label className="invoice-dialog-label">
              Current Due Amount
              <input
                type="text"
                value={formatMoney(getDueAmount(dueDialog.document))}
                readOnly
              />
            </label>
            <label className="invoice-dialog-label">
              Remaining Due Amount
              <input
                type="number"
                min="0.01"
                step="0.01"
                max={getDueAmount(dueDialog.document)}
                value={dueDialog.dueAmount}
                onChange={(event) => setDueDialog((current) => ({ ...current, dueAmount: event.target.value }))}
              />
            </label>
            <label className="invoice-dialog-label">
              Received Amount
              <input
                type="text"
                value={formatMoney(Math.max(0, getDueAmount(dueDialog.document) - Number(dueDialog.dueAmount || 0)))}
                readOnly
              />
            </label>
            <label className="invoice-dialog-label">
              Receipt Account
              <select
                value={dueDialog.receiptAccountCode}
                onChange={(event) => setDueDialog((current) => ({ ...current, receiptAccountCode: event.target.value }))}
              >
                {receiptAccounts.map((account) => (
                  <option key={account.AccountId} value={account.AccountCode}>
                    {account.AccountCode} - {account.AccountName}
                  </option>
                ))}
              </select>
            </label>
            <label className="invoice-dialog-label">
              Due Ref by Employee Name
              <select
                value={dueDialog.employeeName}
                onChange={(event) => setDueDialog((current) => ({ ...current, employeeName: event.target.value }))}
              >
                <option value="">Select employee</option>
                {employees.map((employee) => (
                  <option key={employee.EmployeeId} value={employee.FullName}>
                    {employee.FullName}
                  </option>
                ))}
              </select>
            </label>
            <div className="invoice-dialog-actions">
              <button type="button" className="btn-dialog-cancel" onClick={closeDueDialog}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-dialog-confirm"
                onClick={confirmDueStatus}
                disabled={busyId === dueDialog.document.JobCardId}
              >
                {busyId === dueDialog.document.JobCardId ? 'Saving...' : 'Make Due'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {advanceDialog.open && advanceDialog.document ? (
        <div className="invoice-dialog-backdrop" onClick={closeAdvanceDialog}>
          <div className="invoice-dialog" onClick={(event) => event.stopPropagation()}>
            <h3>Advance Payment</h3>
            <p>
              Receive advance for <strong>#{advanceDialog.document.JobNumber || advanceDialog.document.JobCardId}</strong>.
            </p>
            <label className="invoice-dialog-label">
              Document Type
              <input
                type="text"
                value={getDocumentLabel(advanceDialog.document)}
                readOnly
              />
            </label>
            <label className="invoice-dialog-label">
              Current Amount
              <input
                type="text"
                value={formatMoney(advanceDialog.document.TotalAmount)}
                readOnly
              />
            </label>
            <label className="invoice-dialog-label">
              Already Received
              <input
                type="text"
                value={formatMoney(advanceDialog.document.PaidAmount)}
                readOnly
              />
            </label>
            <label className="invoice-dialog-label">
              Advance Amount
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={advanceDialog.amount}
                onChange={(event) => setAdvanceDialog((current) => ({ ...current, amount: event.target.value }))}
              />
            </label>
            <label className="invoice-dialog-label">
              Receipt Account
              <select
                value={advanceDialog.receiptAccountCode}
                onChange={(event) => setAdvanceDialog((current) => ({ ...current, receiptAccountCode: event.target.value }))}
              >
                {receiptAccounts.map((account) => (
                  <option key={account.AccountId} value={account.AccountCode}>
                    {account.AccountCode} - {account.AccountName}
                  </option>
                ))}
              </select>
            </label>
            <div className="invoice-dialog-actions">
              <button type="button" className="btn-dialog-cancel" onClick={closeAdvanceDialog}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-dialog-confirm btn-dialog-advance"
                onClick={confirmAdvanceDialog}
                disabled={busyId === advanceDialog.document.JobCardId}
              >
                {busyId === advanceDialog.document.JobCardId ? 'Saving...' : 'Receive Advance'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {receiptDialog.open && receiptDialog.document ? (
        <div className="invoice-dialog-backdrop" onClick={closeReceiptDialog}>
          <div className="invoice-dialog" onClick={(event) => event.stopPropagation()}>
            <h3>Receive Due</h3>
            <p>
              Add another payment for <strong>#{receiptDialog.document.JobNumber || receiptDialog.document.JobCardId}</strong>.
            </p>
            <label className="invoice-dialog-label">
              Current Due Amount
              <input
                type="text"
                value={formatMoney(getDueAmount(receiptDialog.document))}
                readOnly
              />
            </label>
            <label className="invoice-dialog-label">
              Receive Amount
              <input
                type="number"
                min="0.01"
                step="0.01"
                max={getDueAmount(receiptDialog.document)}
                value={receiptDialog.amount}
                onChange={(event) => setReceiptDialog((current) => ({ ...current, amount: event.target.value }))}
              />
            </label>
            <label className="invoice-dialog-label">
              Receipt Account
              <select
                value={receiptDialog.receiptAccountCode}
                onChange={(event) => setReceiptDialog((current) => ({ ...current, receiptAccountCode: event.target.value }))}
              >
                {receiptAccounts.map((account) => (
                  <option key={account.AccountId} value={account.AccountCode}>
                    {account.AccountCode} - {account.AccountName}
                  </option>
                ))}
              </select>
            </label>
            <label className="invoice-dialog-label">
              Remaining Due After Receipt
              <input
                type="text"
                value={formatMoney(Math.max(0, getDueAmount(receiptDialog.document) - Number(receiptDialog.amount || 0)))}
                readOnly
              />
            </label>
            <div className="invoice-dialog-actions">
              <button type="button" className="btn-dialog-cancel" onClick={closeReceiptDialog}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-dialog-confirm btn-dialog-receive"
                onClick={confirmReceiptDialog}
                disabled={busyId === receiptDialog.document.JobCardId}
              >
                {busyId === receiptDialog.document.JobCardId ? 'Saving...' : 'Receive Payment'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {paidDialog.open && paidDialog.document ? (
        <div className="invoice-dialog-backdrop" onClick={closePaidDialog}>
          <div className="invoice-dialog" onClick={(event) => event.stopPropagation()}>
            <h3>Make Paid</h3>
            <p>
              Receive full settlement for <strong>#{paidDialog.document.JobNumber || paidDialog.document.JobCardId}</strong>.
            </p>
            <label className="invoice-dialog-label">
              Current Due Amount
              <input
                type="text"
                value={formatMoney(getDueAmount(paidDialog.document))}
                readOnly
              />
            </label>
            <label className="invoice-dialog-label">
              Discount Amount
              <input
                type="number"
                min="0"
                step="0.01"
                max={getDueAmount(paidDialog.document)}
                value={paidDialog.discountAmount}
                onChange={(event) => setPaidDialog((current) => ({ ...current, discountAmount: event.target.value }))}
              />
            </label>
            <label className="invoice-dialog-label">
              Discount Ref Employee
              <select
                value={paidDialog.discountEmployeeName}
                onChange={(event) => setPaidDialog((current) => ({ ...current, discountEmployeeName: event.target.value }))}
              >
                <option value="">Select employee</option>
                {employees.map((employee) => (
                  <option key={employee.EmployeeId} value={employee.FullName}>
                    {employee.FullName}
                  </option>
                ))}
              </select>
            </label>
            <label className="invoice-dialog-label">
              Receive Amount
              <input
                type="text"
                value={formatMoney(Math.max(0, getDueAmount(paidDialog.document) - Number(paidDialog.discountAmount || 0)))}
                readOnly
              />
            </label>
            <label className="invoice-dialog-label">
              Receipt Account
              <select
                value={paidDialog.receiptAccountCode}
                onChange={(event) => setPaidDialog((current) => ({ ...current, receiptAccountCode: event.target.value }))}
              >
                {receiptAccounts.map((account) => (
                  <option key={account.AccountId} value={account.AccountCode}>
                    {account.AccountCode} - {account.AccountName}
                  </option>
                ))}
              </select>
            </label>
            <div className="invoice-dialog-actions">
              <button type="button" className="btn-dialog-cancel" onClick={closePaidDialog}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-dialog-confirm btn-dialog-paid"
                onClick={confirmPaidDialog}
                disabled={busyId === paidDialog.document.JobCardId}
              >
                {busyId === paidDialog.document.JobCardId ? 'Saving...' : 'Make Paid'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
